import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, CheckCircle, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomizePage = () => {
    const [dragActive, setDragActive] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        customer_name: '',
        customer_email: '',
        description: ''
    });

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleFile = (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }
        
        // Validate file size (max 10MB for high quality)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setSelectedImage(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedImage) {
            toast.error('Please upload an image');
            return;
        }
        
        if (!formData.customer_name || !formData.customer_email || !formData.description) {
            toast.error('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            const data = new FormData();
            data.append('image', selectedImage);
            data.append('customer_name', formData.customer_name);
            data.append('customer_email', formData.customer_email);
            data.append('description', formData.description);

            await axios.post(`${API}/customization`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSubmitted(true);
            toast.success('Customization request submitted!');
        } catch (error) {
            console.error('Error submitting customization:', error);
            toast.error('Failed to submit request');
        } finally {
            setLoading(false);
        }
    };

    const removeImage = () => {
        setSelectedImage(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (submitted) {
        return (
            <Layout>
                <div className="pt-32 md:pt-40 pb-16 min-h-screen" data-testid="customize-success">
                    <div className="max-w-xl mx-auto px-4 md:px-8 text-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', duration: 0.5 }}
                        >
                            <CheckCircle className="w-24 h-24 mx-auto mb-6 text-lime" />
                        </motion.div>
                        <h1 className="font-heading text-4xl md:text-6xl mb-4">REQUEST SUBMITTED!</h1>
                        <p className="text-muted-foreground mb-8">
                            We've received your customization request. Our team will review your design and get back to you within 24-48 hours.
                        </p>
                        <button
                            onClick={() => {
                                setSubmitted(false);
                                setSelectedImage(null);
                                setPreviewUrl(null);
                                setFormData({ customer_name: '', customer_email: '', description: '' });
                            }}
                            className="bg-lime text-black px-8 py-4 text-xs font-bold tracking-[0.14em] uppercase"
                        >
                            SUBMIT ANOTHER REQUEST
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16" data-testid="customize-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    {/* Header */}
                    <div className="text-center mb-12 md:mb-16">
                        <span className="text-xs tracking-[0.2em] uppercase text-lime mb-4 block">
                            MAKE IT YOURS
                        </span>
                        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl mb-4">
                            CUSTOMIZE<br />YOUR FIT
                        </h1>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Upload your design and we'll print it on our premium streetwear. 
                            High-quality prints that last. Your vision, our craft.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
                        {/* Upload Section */}
                        <div>
                            <h2 className="font-heading text-2xl mb-6">UPLOAD YOUR DESIGN</h2>
                            
                            {/* Drag & Drop Zone */}
                            <div
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                className={`relative border-2 border-dashed transition-colors ${
                                    dragActive 
                                        ? 'border-lime bg-lime/5' 
                                        : 'border-border hover:border-lime/50'
                                } ${previewUrl ? 'aspect-square' : 'aspect-video'}`}
                                data-testid="upload-zone"
                            >
                                {previewUrl ? (
                                    <div className="relative w-full h-full">
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            className="w-full h-full object-contain bg-black"
                                        />
                                        <button
                                            onClick={removeImage}
                                            className="absolute top-4 right-4 w-10 h-10 bg-black/80 hover:bg-destructive flex items-center justify-center transition-colors"
                                            data-testid="remove-image-btn"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                        <div className="absolute bottom-4 left-4 right-4 bg-black/80 p-3">
                                            <p className="text-sm truncate">{selectedImage?.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {(selectedImage?.size / 1024 / 1024).toFixed(2)} MB
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                                        <Upload className="w-12 h-12 mb-4 text-muted-foreground" />
                                        <p className="font-heading text-xl mb-2">DROP YOUR IMAGE HERE</p>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            or click to browse
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Supports: PNG, JPG, WEBP • Max 10MB • High resolution recommended
                                        </p>
                                    </div>
                                )}
                                
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    data-testid="file-input"
                                />
                            </div>

                            {/* Tips */}
                            <div className="mt-6 bg-card border border-border p-4">
                                <h3 className="font-heading text-sm mb-3">FOR BEST RESULTS:</h3>
                                <ul className="text-xs text-muted-foreground space-y-2">
                                    <li className="flex items-start gap-2">
                                        <ImageIcon className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                                        Use high-resolution images (300 DPI minimum)
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ImageIcon className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                                        PNG with transparent background works best
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ImageIcon className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                                        Avoid copyrighted or trademarked content
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Form Section */}
                        <div>
                            <h2 className="font-heading text-2xl mb-6">YOUR DETAILS</h2>
                            
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                        YOUR NAME *
                                    </label>
                                    <input
                                        type="text"
                                        name="customer_name"
                                        value={formData.customer_name}
                                        onChange={handleChange}
                                        className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                        placeholder="John Doe"
                                        data-testid="customize-name"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                        EMAIL ADDRESS *
                                    </label>
                                    <input
                                        type="email"
                                        name="customer_email"
                                        value={formData.customer_email}
                                        onChange={handleChange}
                                        className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                                        placeholder="john@example.com"
                                        data-testid="customize-email"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                                        DESCRIBE YOUR VISION *
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows={5}
                                        className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime resize-none"
                                        placeholder="Tell us about your design, placement preferences, colors, product type (t-shirt, hoodie, etc.)..."
                                        data-testid="customize-description"
                                    />
                                </div>

                                <div className="bg-lime/10 border border-lime/30 p-4">
                                    <p className="text-sm">
                                        <span className="text-lime font-bold">Note:</span> Our team will review your design and contact you with pricing and timeline within 24-48 hours.
                                    </p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !selectedImage}
                                    className="w-full bg-lime text-black py-4 text-sm font-bold tracking-[0.14em] uppercase hover:tracking-[0.2em] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="submit-customization"
                                >
                                    {loading ? 'SUBMITTING...' : !selectedImage ? 'UPLOAD AN IMAGE FIRST' : 'SUBMIT CUSTOMIZATION REQUEST'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
